"use client";

import { LucideIcon, ChevronRight } from "lucide-react";
import { ComponentType, SVGProps, forwardRef } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";

export type DialogListItemProps = {
  label: string;
  icon?: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  showChevron?: boolean;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  selected?: boolean;
};

/**
 * A selectable list item component designed for use in dialogs with icon, label, and optional chevron
 */
export const DialogListItem = forwardRef<HTMLDivElement, DialogListItemProps>(
  (
    {
      label,
      icon: Icon,
      iconClassName,
      showChevron = true,
      onClick,
      className,
      disabled = false,
      selected = false,
    },
    ref,
  ) => (
    <div
      ref={ref}
      role="option"
      aria-selected={selected}
      aria-disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (disabled) {
          return;
        }
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-lg bg-white py-2 transition-colors focus:outline-none",
        disabled && "cursor-not-allowed opacity-50",
        selected && "bg-neutral-100",
        className,
      )}
    >
      {Icon && <Icon className={cn("h-3 w-3 shrink-0", iconClassName)} />}
      <Typography size="sm" weight="normal" className="flex-1 text-left">
        {label}
      </Typography>
      {showChevron && <ChevronRight className="h-3 w-3 shrink-0" />}
    </div>
  ),
);
DialogListItem.displayName = "DialogListItem";
